import { Analytics } from "@skv/core";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface EoiUrlParams {
  id: string;
  encodedOnlineApplicationId: string;
}

interface OpportunityDetailsEoiRedirectProps {
  partnerName?: string;
  applicationRedirectUrl?: string;
}

const OpportunityDetailsEoiRedirect = (
  props: OpportunityDetailsEoiRedirectProps
): JSX.Element => {
  const { id: routeOppId, encodedOnlineApplicationId } =
    useParams<EoiUrlParams>();

  // Delay redirect to allow for analytics to be sent
  useEffect(() => {
    const timeout = setTimeout(
      () => {
        if (props.applicationRedirectUrl) {
          // Redirect to application form on Third-party website
          window.location.replace(props.applicationRedirectUrl);
        } else {
          // Two-steps redirect to local endpoint to handle redirect with application data
          window.location.replace(
            `/volunteering/${routeOppId}/apply/redirect/${encodedOnlineApplicationId}`
          );
        }
      },
      props.applicationRedirectUrl ? 2000 : 1000
    );

    return () => clearTimeout(timeout);
  }, []);

  const [applicationRedirectLogged, setApplicationRedirectLogged] =
    useState(false);

  const logApplicationRedirect = async () => {
    if (applicationRedirectLogged) return;

    // Log application redirect
    await fetch(`/api/opportunity/${routeOppId}/apply/log-redirect`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({}),
    });
    setApplicationRedirectLogged(true);
  };

  // Third-party application redirect
  if (props.applicationRedirectUrl) {
    logApplicationRedirect();

    return (
      <>
        <p>
          You are being redirected to{" "}
          {props.partnerName ? `${props.partnerName}'s website to ` : ""}
          complete your application for this opportunity.
        </p>
        <Analytics
          data={{
            subSection: "opportunity",
            subSubSection: "opportunity:application+redirect",
            subSubSubSection: "opportunity:application+redirect",
            pageTitle: "opportunity:application+redirect",
            pageType: "opportunity:form",
            opportunityId: Number(routeOppId),
          }}
        />
      </>
    );
  }

  // Two-steps application redirect
  return (
    <>
      <p>Redirecting...</p>
      <Analytics
        data={{
          subSection: "opportunity",
          subSubSection: "opportunity:application+complete",
          subSubSubSection: "opportunity:application+complete",
          pageTitle: "opportunity:application+complete",
          pageType: "opportunity:form",
          opportunityId: Number(routeOppId),
        }}
      />
    </>
  );
};

export default OpportunityDetailsEoiRedirect;
